'use client';

import clsx from 'clsx';
import NextLink from 'next/link';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Icon, Typography } from '@/components/core';
import { useLanguageParam } from '@/hooks';
import { useOutsideClick } from '@/hooks/useClickOutside';

import { AlternateStoryblokStory } from '@/types/common';
import styles from './LanguageSwitcher.module.scss';

type LanguageSwitcherProps = {
    alternates?: AlternateStoryblokStory[];
    className?: string;
    dropdownOpenTop?: boolean;
};

function extractLanguageFromURL(url: string) {
    const languagePattern = /^\/?([a-z]{2})\//i;
    const match = url.match(languagePattern);
    if (match) {
        return match[1];
    }
    return null;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ alternates, className }) => {
    const t = useTranslations('languages');
    const [isOpen, setIsOpen] = useState(false);
    const currentLanguage = useLanguageParam();

    const getLanguageUrl = langCode => {
        const matchingAlternate = alternates?.find(alternate => alternate.full_slug.startsWith(langCode));
        return matchingAlternate ? `/${matchingAlternate.full_slug}` : `/${langCode}`;
    };

    const languageData: Record<string, { name: string; flag: string }> = {
        en: { name: t('en'), flag: '🇬🇧' },
        de: { name: t('de'), flag: '🇩🇪' },
        fr: { name: t('fr'), flag: '🇫🇷' },
        es: { name: t('es'), flag: '🇪🇸' },
        pt: { name: t('pt'), flag: '🇵🇹' },
        it: { name: t('it'), flag: '🇮🇹' },
    };
    const ref = useOutsideClick(() => setIsOpen(false));

    if (!alternates.length) return null;

    return (
        <div
            onClick={() => setIsOpen(!isOpen)}
            className={clsx(styles.root, className, {
                [styles.open]: isOpen,
            })}
        >
            <div
                ref={ref}
                className={clsx(styles.wrapper)}
            >
                <div className={styles.currentLang}>
                    <Typography looksLike="menu">{currentLanguage}</Typography>
                    <Icon
                        name="chevron"
                        className={clsx(styles.icon, { [styles.reverse]: !isOpen })}
                    />
                </div>

                <div className={clsx(styles.contentWrapper)}>
                    {alternates.map(lang => (
                        <NextLink
                            className={styles.link}
                            href={getLanguageUrl(lang.full_slug)}
                            key={lang.slug}
                        >
                            {(() => {
                                const code = extractLanguageFromURL(lang.full_slug) || lang.slug;
                                const { name, flag } = languageData[code] || { name: code, flag: code };
                                return (
                                    <span title={name}>
                                        <span className={styles.flag}>{flag}</span> - {code.toUpperCase()}
                                    </span>
                                );
                            })()}
                        </NextLink>
                    ))}
                </div>
            </div>
        </div>
    );
};
